import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { useAuthStore, useCartStore } from '@/lib/store'
import { ShoppingCart, DollarSign, Trash2, Plus, Minus, CheckCircle, CreditCard, Truck, User, Shield, AlertCircle, ArrowRight, ChevronDown, Phone } from 'lucide-react'
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js'
import { CurrencyCode, CURRENCY_NAMES, convertCurrency, formatCurrency, getExchangeRate } from '@/lib/currency'
import { AEMCashForm, AEMCashCardData } from '@/components/payment/AEMCashForm'

interface DeliveryInfo {
  address: string
  city: string
  zipCode: string
  phone: string
}


async function getDeliveryFeeFromCities(
  sellerCity: string,
  sellerCountry: string,
  buyerCity: string,
  buyerCountry: string
): Promise<number> {
  if (sellerCountry.toLowerCase() !== buyerCountry.toLowerCase()) {
    return -1 // Livraison internationale non disponible
  }

  const apiKey = import.meta.env.VITE_ORS_API_KEY

  const getCoords = async (city: string, country: string) => {
    const res = await fetch(`https://api.openrouteservice.org/geocode/search?api_key=${apiKey}&text=${city}, ${country}`)
    const data = await res.json()
    return data.features[0]?.geometry.coordinates
  }

  const sellerCoords = await getCoords(sellerCity, sellerCountry)
  const buyerCoords = await getCoords(buyerCity, buyerCountry)

  if (!sellerCoords || !buyerCoords) return 6.50

  const routeRes = await fetch("https://api.openrouteservice.org/v2/directions/driving-car", {
    method: "POST",
    headers: {
      "Authorization": apiKey,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ coordinates: [sellerCoords, buyerCoords] })
  })

  if (!routeRes?.ok) return 6.50
  const routeData = await routeRes.json()
  const distanceKm = routeData.routes[0].summary.distance / 1000
  let fee = distanceKm * 0.5
  if (fee < 6.50) fee = 6.50
  if (fee > 15.00) fee = 15.00
  return parseFloat(fee.toFixed(2))
}


export function CartPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const { items, removeItem, updateQuantity, total, clearCart } = useCartStore()
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [step, setStep] = useState<'cart' | 'delivery' | 'payment'>('cart')
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo>({
    address: '',
    city: '',
    zipCode: '',
    phone: ''
  })
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false)
  const [paymentType, setPaymentType] = useState<'paypal' | 'aemcash' | 'in_person'>('paypal')
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>('USD')
  const [showCurrencySelector, setShowCurrencySelector] = useState(false)

  // Check if user is logged in when moving to payment step
  useEffect(() => {
    if (step === 'payment' && !user) {
      setShowLoginPrompt(true)
      setStep('delivery')
    }
  }, [step, user])

  if (!items.length) {
    return (
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
          <h2 className="mt-2 text-lg font-medium text-gray-900">Votre panier est vide</h2>
          <p className="mt-1 text-gray-500">Ajoutez des produits pour commencer vos achats</p>
          <Link to="/products">
            <Button variant="primary" className="mt-4">
              Parcourir les produits
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const subtotal = total()
  const deliveryFee = 2.00
  const tax = 1.99
  // Calculate PayPal fee: 2.9% of subtotal + $0.30
  const paypalFee = (subtotal * 0.029) + 0.30
  const finalTotal = subtotal + deliveryFee + tax + paypalFee

  const handlePaypalCreateOrder = (data: any, actions: any) => {
    return actions.order.create({
      purchase_units: [{
        amount: {
          value: finalTotal.toFixed(2),
          currency_code: "USD",
          breakdown: {
            item_total: {
              currency_code: "USD",
              value: subtotal.toFixed(2)
            },
            shipping: {
              currency_code: "USD",
              value: deliveryFee.toFixed(2)
            },
            tax_total: {
              currency_code: "USD",
              value: tax.toFixed(2)
            },
            handling: {
              currency_code: "USD",
              value: paypalFee.toFixed(2)
            }
          }
        }
      }]
    })
  }

  const createOrderInDatabase = async (paypalOrderId: string = '') => {
    if (!user) {
      console.error('User not authenticated');
      setShowLoginPrompt(true);
      return false;
    }

    try {
      // For each item in the cart, create an order
      for (const item of items) {
        const fullDeliveryAddress = `${deliveryInfo.address}, ${deliveryInfo.city}, ${deliveryInfo.zipCode}`;
        
        // Get the seller ID from the product
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('seller_id')
          .eq('id', item.product.id)
          .single();
          
        if (productError) {
          console.error('Error fetching product seller:', productError);
          continue;
        }
        
        // Create the order
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .insert([{
            buyer_id: user.id,
            seller_id: productData.seller_id,
            product_id: item.product.id,
            final_price: item.negotiatedPrice || item.product.price * item.quantity,
            status: paymentType === 'online' ? 'paid' : 'confirmed', // Set as paid for online, confirmed for in-person
            delivery_type: 'standard', // Default to standard delivery
            delivery_address: fullDeliveryAddress,
            payment_type: paymentType,
            // For in-person payments, confirmation_code will be generated by the trigger
          }])
          .select();

        if (orderError) {
          console.error('Error creating order:', orderError);
          return false;
        }
        
        // Create notification for seller
        await supabase
          .from('notifications')
          .insert([{
            user_id: productData.seller_id,
            title: 'Nouvelle commande',
            content: `Vous avez reçu une nouvelle commande pour "${item.product.title}"`,
            type: 'order',
            metadata: {
              order_id: orderData?.[0]?.id,
              product_id: item.product.id,
              buyer_id: user.id,
              payment_type: paymentType
            }
          }]);
          
        // Create notification for buyer
        await supabase
          .from('notifications')
          .insert([{
            user_id: user.id,
            title: 'Commande confirmée',
            content: `Votre commande pour "${item.product.title}" a été confirmée`,
            type: 'order',
            metadata: {
              order_id: orderData?.[0]?.id,
              product_id: item.product.id,
              seller_id: productData.seller_id,
              payment_type: paymentType
            }
          }]);
      }
      
      return true;
    } catch (error) {
      console.error('Error creating order in database:', error);
      return false;
    }
  }

  const handlePaypalOnApprove = async (data: any, actions: any) => {
    try {
      const order = await actions.order.capture()
      
      // Create order in our database
      const orderCreated = await createOrderInDatabase(data.orderID);
      
      if (!orderCreated) {
        setToastMessage('Paiement réussi mais erreur lors de la création de la commande. Contactez le support.');
        setToastType('error');
        setShowToast(true);
        return;
      }
      
      setShowPaymentSuccess(true)
      clearCart()
      
      // Redirect after 3 seconds
      setTimeout(() => {
        setShowPaymentSuccess(false)
        navigate('/products')
      }, 3000)
    } catch (error) {
      console.error('Payment failed:', error)
      setToastMessage('Le paiement a échoué. Veuillez réessayer.')
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleInPersonPayment = async () => {
    try {
      setLoading(true);

      if (!user) {
        setShowLoginPrompt(true);
        setLoading(false);
        return;
      }

      const orderCreated = await createOrderInDatabase();

      if (!orderCreated) {
        setToastMessage('Erreur lors de la création de la commande. Veuillez réessayer.');
        setToastType('error');
        setShowToast(true);
        setLoading(false);
        return;
      }

      setShowPaymentSuccess(true);
      clearCart();

      setTimeout(() => {
        setShowPaymentSuccess(false);
        navigate('/profile');
      }, 3000);
    } catch (error) {
      console.error('Order creation failed:', error);
      setToastMessage('La création de la commande a échoué. Veuillez réessayer.');
      setToastType('error');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAEMCashPayment = async (cardData: AEMCashCardData) => {
    try {
      setLoading(true);

      if (!user) {
        setShowLoginPrompt(true);
        setLoading(false);
        return;
      }

      const aemCashPayload = {
        cardNumber: cardData.cardNumber,
        cvc: cardData.cvc,
        expirationMonth: cardData.expirationMonth,
        expirationYear: cardData.expirationYear,
        cardholderName: cardData.cardholderName,
        amount: finalTotal,
        currency: 'USD',
        deliveryInfo: deliveryInfo,
        items: items.map(item => ({
          product_id: item.product.id,
          title: item.product.title,
          quantity: item.quantity,
          price: item.negotiatedPrice || item.product.price
        }))
      };

      const response = await fetch('https://aemcash.com/api/process-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_AEMCASH_API_KEY}`,
        },
        body: JSON.stringify(aemCashPayload)
      });

      const result = await response.json();

      if (!response.ok || result.status !== 'success') {
        throw new Error(result.message || 'Paiement refusé');
      }

      const orderCreated = await createOrderInDatabase(result.transaction_id);

      if (!orderCreated) {
        setToastMessage('Paiement réussi mais erreur lors de la création de la commande. Contactez le support.');
        setToastType('error');
        setShowToast(true);
        setLoading(false);
        return;
      }

      setShowPaymentSuccess(true);
      clearCart();

      setTimeout(() => {
        setShowPaymentSuccess(false);
        navigate('/products');
      }, 3000);
    } catch (error: any) {
      console.error('AEM Cash payment failed:', error);
      setToastMessage(error.message || 'Le paiement a échoué. Veuillez vérifier vos informations et réessayer.');
      setToastType('error');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeliverySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Check if user is logged in before proceeding to payment
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    
    setStep('payment')
  }

  const renderStep = () => {
    switch (step) {
      case 'cart':
        return (
          <div className="space-y-4">
            {items.map((item) => {
              const itemPrice = item.negotiatedPrice || item.product.price * item.quantity;
              // Get the converted price based on the selected currency
              const convertedPrice = convertCurrency(itemPrice, selectedCurrency);
              
              return (
                <div
                  key={item.product.id}
                  className="bg-white rounded-lg shadow p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center"
                >
                  <img
                    src={item.product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                    alt={item.product.title}
                    className="w-full sm:w-24 h-24 object-cover rounded mb-4 sm:mb-0"
                  />
                  <div className="sm:ml-6 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {item.product.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      ${item.product.price}
                    </p>
                    
                    {selectedCurrency !== 'USD' && (
                      <div className="mt-1 text-xs text-blue-600">
                        {formatCurrency(convertCurrency(item.product.price, selectedCurrency), selectedCurrency)}
                      </div>
                    )}
                    
                    <div className="mt-4 flex flex-wrap items-center gap-4">
                      <div className="flex items-center border rounded">
                        <button
                          className="p-2"
                          onClick={() =>
                            updateQuantity(item.product.id, Math.max(0, item.quantity - 1))
                          }
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="px-4">{item.quantity}</span>
                        <button
                          className="p-2"
                          onClick={() =>
                            updateQuantity(item.product.id, item.quantity + 1)
                          }
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.product.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
            <Button
              variant="primary"
              className="w-full"
              onClick={() => setStep('delivery')}
            >
              Continuer vers la livraison
            </Button>
          </div>
        )

      case 'delivery':
        return (
          <form onSubmit={handleDeliverySubmit} className="space-y-6">
            {showLoginPrompt && (
              <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Connexion requise</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Vous devez être connecté pour finaliser votre commande.</p>
                      <div className="mt-4 flex space-x-4">
                        <Link to="/login" className="text-sm font-medium text-primary hover:text-primary-dark">
                          Se connecter
                        </Link>
                        <Link to="/register" className="text-sm font-medium text-primary hover:text-primary-dark">
                          Créer un compte
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Adresse
              </label>
              <input
                type="text"
                required
                value={deliveryInfo.address}
                onChange={(e) => setDeliveryInfo(prev => ({ ...prev, address: e.target.value }))}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Ville
                </label>
                <input
                  type="text"
                  required
                  value={deliveryInfo.city}
                  onChange={(e) => setDeliveryInfo(prev => ({ ...prev, city: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Code Postal
                </label>
                <input
                  type="text"
                  required
                  value={deliveryInfo.zipCode}
                  onChange={(e) => setDeliveryInfo(prev => ({ ...prev, zipCode: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Téléphone
              </label>
              <input
                type="tel"
                required
                value={deliveryInfo.phone}
                onChange={(e) => setDeliveryInfo(prev => ({ ...prev, phone: e.target.value }))}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              className="w-full"
            >
              Continuer vers le paiement
            </Button>
          </form>
        )

      case 'payment':
        return (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Choisissez votre méthode de paiement
              </h3>
              
              <div className="space-y-4">
                <div
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    paymentType === 'paypal'
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setPaymentType('paypal')}
                >
                  <div className="flex-shrink-0">
                    <CreditCard className={`h-6 w-6 ${paymentType === 'paypal' ? 'text-primary' : 'text-gray-400'}`} />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-medium text-gray-900">PayPal</h4>
                    <p className="text-sm text-gray-500">Payez avec votre compte PayPal</p>
                  </div>
                </div>

                <div
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    paymentType === 'aemcash'
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setPaymentType('aemcash')}
                >
                  <div className="flex-shrink-0">
                    <CreditCard className={`h-6 w-6 ${paymentType === 'aemcash' ? 'text-primary' : 'text-gray-400'}`} />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-medium text-gray-900">AEM Cash</h4>
                    <p className="text-sm text-gray-500">Payez par carte bancaire via aemcash.com</p>
                  </div>
                </div>
                
                <div 
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    paymentType === 'in_person' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setPaymentType('in_person')}
                >
                  <div className="flex-shrink-0">
                    <Truck className={`h-6 w-6 ${paymentType === 'in_person' ? 'text-primary' : 'text-gray-400'}`} />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-medium text-gray-900">Paiement à la livraison</h4>
                    <p className="text-sm text-gray-500">Payez en espèces à la réception</p>
                  </div>
                </div>
                  
                  {/* MonCash/NatCash Notice */}
                  <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-yellow-800 flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-yellow-600" />
                      Paiement par MonCash ou NatCash
                    </h5>
                    <p className="mt-2 text-sm text-yellow-700">
                      Si vous n'avez pas de carte bancaire et souhaitez payer par MonCash ou NatCash, 
                      veuillez appeler l'un de ces numéros pour effectuer votre transfert:
                    </p>
                    <div className="mt-3 flex flex-col space-y-2">
                      <a href="tel:+50947734531" className="text-sm font-medium text-primary hover:underline flex items-center">
                        <Phone className="h-4 w-4 mr-2" />
                        +509 47734531
                      </a>
                      <a href="tel:+50946501040" className="text-sm font-medium text-primary hover:underline flex items-center">
                        <Phone className="h-4 w-4 mr-2" />
                        +509 46501040
                      </a>
                    </div>
                    <p className="mt-3 text-xs text-yellow-600">
                      Après avoir effectué le transfert, sélectionnez "Paiement à la livraison" ci-dessus et 
                      mentionnez votre paiement MonCash/NatCash dans les notes de commande.
                    </p>
                  </div>
              </div>
              
              {paymentType === 'in_person' && (
                <div className="mt-6">
                  <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-yellow-700">
                      <strong>Important:</strong> Un code de confirmation unique vous sera fourni. 
                      Vous devrez le présenter au livreur lors de la livraison pour finaliser la transaction.
                    </p>
                  </div>
                  
                  {/* AVERTISSEMENT payment availability */}
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                      <p className="text-sm text-red-700">
                        <strong>AVERTISSEMENT :</strong> Le paiement en présentiel est uniquement disponible pour les clients situés en Haïti et en République Dominicaine. Pour tous les autres pays, seul le paiement en ligne est accepté. Merci de votre compréhension.
                      </p>
                    </div>
                  </div>
                  
                  {/* Currency selector for in-person payments */}
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-blue-800">
                        Paiement en devises multiples
                      </h4>
                      <div className="relative">
                        <select
                          value={selectedCurrency}
                          onChange={(e) => setSelectedCurrency(e.target.value as CurrencyCode)}
                          className="block w-full pl-3 pr-10 py-2 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                        >
                          <option value="USD">Dollar américain (USD)</option>
                          <option value="HTG">Gourde haïtienne (HTG)</option>
                          <option value="DOP">Peso dominicain (DOP)</option>
                          <option value="EUR">Euro (EUR)</option>
                          <option value="CAD">Dollar canadien (CAD)</option>
                          <option value="JMD">Dollar jamaïcain (JMD)</option>
                        </select>
                      </div>
                    </div>
                    
                    {selectedCurrency !== 'USD' && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800">Montant total à payer :</p>
                        <div className="mt-1 grid grid-cols-2 gap-4">
                          <div className="text-sm">
                            <span className="font-medium">{formatCurrency(finalTotal, 'USD')}</span>
                          </div>
                          <div className="text-sm">
                            <span className="font-medium">{formatCurrency(convertCurrency(finalTotal, selectedCurrency), selectedCurrency)}</span>
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-blue-600">
                          Taux de change : {getExchangeRate(selectedCurrency)}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleInPersonPayment}
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Confirmer la commande'
                    )}
                  </Button>
                </div>
              )}
              
              {paymentType === 'paypal' && (
                <PayPalScriptProvider options={{
                  clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID,
                  currency: "USD",
                  components: "buttons",
                  intent: "capture",
                  disableFunding: "paylater"
                }}>
                  <div className="mt-6">
                    <PayPalButtons
                      createOrder={handlePaypalCreateOrder}
                      onApprove={handlePaypalOnApprove}
                      style={{
                        layout: "vertical",
                        shape: "rect",
                        label: "pay"
                      }}
                    />
                  </div>
                </PayPalScriptProvider>
              )}

              {paymentType === 'aemcash' && (
                <div className="mt-6">
                  <AEMCashForm
                    onSubmit={handleAEMCashPayment}
                    loading={loading}
                    totalAmount={finalTotal}
                  />
                </div>
              )}
            </div>
          </div>
        )
    }
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
          position="center"
        />
      )}

      {/* Payment Success Modal */}
      {showPaymentSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
          <div className="relative bg-white rounded-xl p-8 max-w-md w-full m-4 transform transition-all shadow-xl border-2 border-green-100 animate-in fade-in zoom-in duration-300">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-xl font-semibold text-gray-900">
                {paymentType === 'in_person' ? 'Commande confirmée !' : 'Paiement réussi !'}
              </h3>
              <div className="mt-3">
                <p className="text-sm text-gray-600">
                  {paymentType === 'in_person'
                    ? 'Votre commande a été confirmée. Vous recevrez un code de confirmation que vous devrez présenter au livreur lors de la livraison.'
                    : 'Votre commande a été confirmée et sera traitée rapidement. Vous recevrez un email de confirmation avec les détails de votre commande.'}
                </p>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                Redirection vers la page des produits...
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="lg:grid lg:grid-cols-12 lg:gap-x-12 lg:items-start">
        <div className="lg:col-span-7">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  step === 'cart' ? 'bg-primary text-white' : 'bg-gray-200'
                }`}>
                  <ShoppingCart className="h-5 w-5" />
                </div>
                <div className={`h-1 w-12 ${
                  step === 'delivery' || step === 'payment' ? 'bg-primary' : 'bg-gray-200'
                }`} />
              </div>
              <div className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  step === 'delivery' ? 'bg-primary text-white' : 'bg-gray-200'
                }`}>
                  <Truck className="h-5 w-5" />
                </div>
                <div className={`h-1 w-12 ${
                  step === 'payment' ? 'bg-primary' : 'bg-gray-200'
                }`} />
              </div>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step === 'payment' ? 'bg-primary text-white' : 'bg-gray-200'
              }`}>
                <DollarSign className="h-5 w-5" />
              </div>
            </div>
          </div>

          {/* Current Step Content */}
          {renderStep()}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-5">
          <div className="bg-white shadow rounded-lg p-6 mt-8 lg:mt-0">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Résumé de la commande</h2>
              
              {/* Currency selector dropdown */}
              {step === 'cart' && (
                <div className="relative">
                  <div onClick={() => setShowCurrencySelector(!showCurrencySelector)} 
                       className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors text-sm">
                    <span>{selectedCurrency}</span>
                    <ChevronDown className={`h-4 w-4 text-gray-400 ml-2 transition-transform ${showCurrencySelector ? 'transform rotate-180' : ''}`} />
                  </div>
                  
                  {showCurrencySelector && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                      <div className="py-1">
                        <p className="px-4 py-2 text-xs font-medium text-gray-500">Choisir une devise</p>
                        <button 
                          onClick={() => { setSelectedCurrency('USD'); setShowCurrencySelector(false); }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedCurrency === 'USD' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          Dollar américain (USD)
                        </button>
                        <button 
                          onClick={() => { setSelectedCurrency('HTG'); setShowCurrencySelector(false); }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedCurrency === 'HTG' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          Gourde haïtienne (HTG)
                        </button>
                        <button 
                          onClick={() => { setSelectedCurrency('DOP'); setShowCurrencySelector(false); }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedCurrency === 'DOP' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          Peso dominicain (DOP)
                        </button>
                        <button 
                          onClick={() => { setSelectedCurrency('EUR'); setShowCurrencySelector(false); }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedCurrency === 'EUR' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          Euro (EUR)
                        </button>
                        <button 
                          onClick={() => { setSelectedCurrency('CAD'); setShowCurrencySelector(false); }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedCurrency === 'CAD' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          Dollar canadien (CAD)
                        </button>
                        <button 
                          onClick={() => { setSelectedCurrency('JMD'); setShowCurrencySelector(false); }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedCurrency === 'JMD' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          Dollar jamaïcain (JMD)
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Sous-total</span>
                <div>
                  <span className="text-gray-900">${subtotal.toFixed(2)}</span>
                  {selectedCurrency !== 'USD' && (
                    <div className="text-xs text-blue-600 text-right mt-1">
                      {formatCurrency(convertCurrency(subtotal, selectedCurrency), selectedCurrency)}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Frais de livraison</span>
                <div>
                  <span className="text-gray-900">${deliveryFee.toFixed(2)}</span>
                  {selectedCurrency !== 'USD' && (
                    <div className="text-xs text-blue-600 text-right mt-1">
                      {formatCurrency(convertCurrency(deliveryFee, selectedCurrency), selectedCurrency)}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Taxes</span>
                <div>
                  <span className="text-gray-900">${tax.toFixed(2)}</span>
                  {selectedCurrency !== 'USD' && (
                    <div className="text-xs text-blue-600 text-right mt-1">
                      {formatCurrency(convertCurrency(tax, selectedCurrency), selectedCurrency)}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Frais PayPal (2.9% + $0.30)</span>
                <div>
                  <span className="text-gray-900">${paypalFee.toFixed(2)}</span>
                  {selectedCurrency !== 'USD' && (
                    <div className="text-xs text-blue-600 text-right mt-1">
                      {formatCurrency(convertCurrency(paypalFee, selectedCurrency), selectedCurrency)}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between font-medium">
                  <span className="text-gray-900">Total</span>
                  <div>
                    <span className="text-gray-900">${finalTotal.toFixed(2)}</span>
                    {selectedCurrency !== 'USD' && (
                      <div className="text-sm text-blue-700 font-medium text-right mt-1">
                        {formatCurrency(convertCurrency(finalTotal, selectedCurrency), selectedCurrency)}
                      </div>
                    )}
                  </div>
                </div>
                
                {selectedCurrency !== 'USD' && (
                  <div className="mt-2 text-xs text-gray-500 text-right">
                    Taux : {getExchangeRate(selectedCurrency)}
                  </div>
                )}
              </div>
            </div>

            {step !== 'cart' && (
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900">Informations de livraison</h3>
                {deliveryInfo.address && (
                  <div className="mt-2 text-sm text-gray-600">
                    <p>{deliveryInfo.address}</p>
                    <p>{deliveryInfo.city}, {deliveryInfo.zipCode}</p>
                    <p>{deliveryInfo.phone}</p>
                  </div>
                )}
              </div>
            )}
            
            {!user && (
              <div className="mt-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-start">
                  <User className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Connexion requise</h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      Vous devez être connecté pour finaliser votre commande.
                    </p>
                    <div className="mt-3 flex space-x-4">
                      <Link to="/login" className="text-sm font-medium text-primary hover:text-primary-dark">
                        Se connecter
                      </Link>
                      <Link to="/register" className="text-sm font-medium text-primary hover:text-primary-dark">
                        Créer un compte
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}